import React from "react";
import { Link } from "react-router-dom";
import { BrandMark } from "./BrandMark";
import { useAuth } from "../contexts/AuthContext";
import { tierLabel } from "../lib/tier";

/**
 * AuthedPlaceholder — used by Session 1's customer/staff/admin route shells
 * once auth is in place. Says hello to the user, shows their role + tier,
 * notes which session ships the real page, and offers a logout link.
 */
export const AuthedPlaceholder = ({ title, session, description, testId }) => {
  const { user, logout } = useAuth();
  const greet = user?.first_name ? `Welcome, ${user.first_name}.` : "Welcome.";

  return (
    <main
      data-testid={testId || "authed-placeholder"}
      className="min-h-screen bg-napoli-cream text-napoli-espresso flex flex-col items-center justify-center px-6 py-16"
    >
      <div className="w-full max-w-2xl flex flex-col items-center text-center animate-fade-up">
        <BrandMark size="md" theme="light" to="/" linkTestId="authed-placeholder-brand-link" />

        <p className="mt-10 font-flourish text-napoli-gold leading-none text-[44px]">
          {greet}
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          {user?.role ? (
            <span className="font-body text-[10px] tracking-[0.28em] uppercase text-napoli-oxblood border border-napoli-oxblood/40 px-3 py-1">
              {user.role}
            </span>
          ) : null}
          {user?.current_tier ? (
            <span className="font-body text-[10px] tracking-[0.28em] uppercase text-napoli-espresso border border-napoli-gold/60 px-3 py-1">
              {user.current_tier}
            </span>
          ) : null}
          {typeof user?.points_balance === "number" ? (
            <span className="font-body text-[10px] tracking-[0.28em] uppercase text-napoli-espresso-soft">
              {user.points_balance} pts
            </span>
          ) : null}
        </div>

        <h1
          data-testid="authed-placeholder-title"
          className="mt-8 font-display font-bold text-3xl sm:text-4xl text-napoli-espresso"
        >
          {title}
        </h1>
        {description ? (
          <p className="mt-4 font-body text-[15px] text-napoli-espresso-soft max-w-md leading-relaxed">
            {description}
          </p>
        ) : null}

        <div
          data-testid="authed-placeholder-session-tag"
          className="mt-8 inline-flex items-center gap-2 border border-napoli-gold/60 px-5 py-2"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-napoli-gold" aria-hidden />
          <span className="font-body text-xs tracking-[0.24em] uppercase text-napoli-espresso-soft">
            Coming in Session {session}
          </span>
        </div>

        <div className="mt-12 flex items-center gap-6">
          <Link
            to="/"
            className="font-body text-[13px] tracking-[0.18em] uppercase text-napoli-oxblood hover:text-napoli-oxblood-dark"
          >
            ← Home
          </Link>
          <button
            type="button"
            onClick={() => logout("/")}
            data-testid="authed-placeholder-logout"
            className="font-body text-[13px] text-napoli-oxblood underline underline-offset-4 hover:text-napoli-oxblood-dark"
          >
            Log out
          </button>
        </div>
      </div>
    </main>
  );
};

export default AuthedPlaceholder;
